// 인원별 월 요금표 - 요금 페이지(/price), 메인 요금 섹션, 앱제작신청(/inquiry), 결제(/payment)가 함께 쓴다.
// 서버 server/routers/common/pricing.js의 PLAN_TIERS와 숫자·문자열을 동일하게 유지할 것
// (size 문자열은 신청서의 성도 규모 값으로 그대로 저장되고 서버가 이 값으로 금액을 재계산한다).

export type OptionalApp = '관리자앱' | '교적부앱' | '재정앱' | '주일학교앱';

export interface PlanTier {
  size: string;
  base: number; // 성도용 앱(기본) + PC 관리자 월 요금
  apps: Record<OptionalApp, number>; // 앱별 월 추가 요금
}

function tier(size: string, base: number, adminRoll: number, financeSs: number): PlanTier {
  return {
    size,
    base,
    apps: { 관리자앱: adminRoll, 교적부앱: adminRoll, 재정앱: financeSs, 주일학교앱: financeSs },
  };
}

export const PLAN_TIERS: PlanTier[] = [
  tier('100명 이하', 30000, 10000, 20000),
  tier('300명 이하', 40000, 20000, 30000),
  tier('500명 이하', 50000, 30000, 50000),
  tier('1,000명 이하', 70000, 50000, 70000),
  tier('5,000명 이하', 100000, 70000, 100000),
  tier('1만 명 이하', 150000, 100000, 150000),
  tier('1만 명 초과', 200000, 150000, 200000),
];

export const OPTIONAL_APPS: { key: OptionalApp; label: string; desc: string }[] = [
  { key: '관리자앱', label: '관리자 앱', desc: '예배순서·소식·설교·갤러리를 폰에서 관리' },
  { key: '교적부앱', label: '교적부 앱', desc: '교적·출석부·심방 관리' },
  { key: '재정앱', label: '재정 앱', desc: '수입·지출·예산 관리' },
  { key: '주일학교앱', label: '주일학교 앱', desc: '출석 체크·알림장·학부모 소통' },
];

// 모든 서비스 플랜의 신청 기본요금
export const APPLICATION_DEPOSIT = 100000;

export function findPlanTier(size: string): PlanTier | undefined {
  return PLAN_TIERS.find((item) => item.size === size);
}

// 규모를 모르면(미선택·옛 신청) null
export function computeMonthlyFee(size: string, apps: string[]): number | null {
  const found = findPlanTier(size);
  if (!found) return null;
  return OPTIONAL_APPS.reduce(
    (sum, app) => (apps.includes(app.key) ? sum + found.apps[app.key] : sum),
    found.base
  );
}

// 30000 -> '3만 원', 150000 -> '15만 원'
export function formatManwon(amount: number): string {
  return amount % 10000 === 0 ? `${amount / 10000}만 원` : `${amount.toLocaleString()}원`;
}

// 요금표 표시용 [인원, 기본 월 요금, 관리자·교적부 앱별 추가, 재정·주일학교 앱별 추가]
export const MONTHLY_PLANS: [string, string, string, string][] = PLAN_TIERS.map((item) => [
  item.size,
  `월 ${formatManwon(item.base)}`,
  `+${formatManwon(item.apps.관리자앱)}`,
  `+${formatManwon(item.apps.재정앱)}`,
]);

export const SERVER_PLANS: [string, string][] = [
  ['500명 이하', '단독 서버 없음'],
  ['500명 이상', '3백만 원'],
  ['1000명 이상', '5백만 원'],
  ['1만명 이상', '1천만 원']
];

// 단독 서버 구축 시 교회가 별도로 부담하는 비용 [항목, 주기, 비용]
export const SERVER_COST_ROWS: [string, string, string][] = [
  ['도메인', '연', '21,000~24,000원'],
  ['인증서', '연', '약 40,000원'],
  ['서버비', '월', '서버 속도에 따라 상이 (월8만원부터)']
];

// 성도 규모(PLAN_TIERS.size) → SERVER_PLANS 행. 500명 이하는 단독 서버 없음(공용 서버).
const SERVER_PLAN_BY_SIZE: Record<string, number> = {
  '100명 이하': 0,
  '300명 이하': 0,
  '500명 이하': 0,
  '1,000명 이하': 1,
  '5,000명 이하': 2,
  '1만 명 이하': 2,
  '1만 명 초과': 3,
};

// 규모에 해당하는 단독 서버 구축 안내. 규모를 모르면 null, 단독 서버가 없는 규모면 needed=false.
export function findServerPlan(size: string): { needed: boolean; label: string; amount: string } | null {
  const index = SERVER_PLAN_BY_SIZE[size];
  if (index === undefined) return null;
  const [label, amount] = SERVER_PLANS[index];
  return { needed: index > 0, label, amount };
}
